'use client';

import type { GetUserDesignsParams } from '@/api/getUserDesigns';
import CommonLoading from '@/components/layouts/CommonLoading/CommonLoading';
import { InfiniteScroll } from '@/components/ui/InfiniteScroll/InfiniteScroll';
import ItemFilterTrigger from '@/components/ui/ItemFilterTrigger/ItemFilterTrigger';
import SectionTitle from '@/components/ui/SectionTitle/SectionTitle';
import UserDesignCard from '@/components/ui/UserDesignCard/UserDesignCard';
import type { SizeSelectFrom } from '@/features/gfUserDesign/constants';
import { useUserDesignInfiniteScroll } from '@/features/gfUserDesign/hooks/useUserDesignInfiniteScroll';
import type { UserDesignItem } from '@/types';
import { getImageLayoutSizes } from '@/utils/nextImage';
import type { Route } from 'next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import UserDesignDetailModal from '../UserDesignDetailModal/UserDesignDetailModal';
import styles from './UserDesignGridSection.module.scss';

type CommonProps = {
  designs: UserDesignItem[];
  hasMore: boolean;
  apiParams?: Omit<GetUserDesignsParams, 'lastId' | 'direction'>;
  callbackUrlBase: string;
  sizeSelectFrom: SizeSelectFrom;
  initialOpenUserPrintId?: string;
};

type GlobalVariantProps = CommonProps & {
  variant: 'global';
};

type WorkVariantProps = CommonProps & {
  variant: 'work';
  onOpenItemFilter: () => void;
  selectedProductName?: string;
  onRemoveItemFilter: () => void;
  isFilterPending: boolean;
};

type UserDesignGridSectionProps = GlobalVariantProps | WorkVariantProps;

function UserDesignGridSection(props: UserDesignGridSectionProps) {
  const {
    designs,
    hasMore: initialHasMore,
    apiParams,
    variant,
    callbackUrlBase,
    sizeSelectFrom,
    initialOpenUserPrintId,
  } = props;

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { visibleDesigns, hasMore, isLoading, loadMore } = useUserDesignInfiniteScroll({
    initialDesigns: designs,
    initialHasMore: initialHasMore,
    apiParams,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    if (searchParams.has('openModal')) {
      const params = new URLSearchParams(searchParams);
      params.delete('openModal');
      const query = params.toString();
      router.replace((query ? `${pathname}?${query}` : pathname) as Route, { scroll: false });
    }
  }, [searchParams, router, pathname]);

  // openModal パラメータがある場合、対象デザインを検索してモーダルを自動起動
  // 初期データに含まれない場合は追加ページを読み込むが、上限を設けて無限リクエストを防止
  const autoLoadCountRef = useRef(0);
  const maxAutoLoadPages = 5;

  useEffect(() => {
    if (!initialOpenUserPrintId || visibleDesigns.length === 0) return;

    const index = visibleDesigns.findIndex((d) => d.userPrintId === initialOpenUserPrintId);
    if (index >= 0) {
      setSelectedIndex(index);
      setIsModalOpen(true);
      return;
    }

    if (hasMore && autoLoadCountRef.current < maxAutoLoadPages) {
      autoLoadCountRef.current += 1;
      loadMore();
    }
  }, [initialOpenUserPrintId, visibleDesigns, hasMore, loadMore]);

  const handleDesignClick = (design: UserDesignItem) => {
    const index = visibleDesigns.findIndex((d) => d.id === design.id);
    if (index >= 0) {
      setSelectedIndex(index);
      setIsModalOpen(true);
    }
  };

  const getCallbackUrl = useCallback(
    (item: UserDesignItem) => `${callbackUrlBase}?openModal=${item.userPrintId}` as Route,
    [callbackUrlBase],
  );

  const gridClassName = `${styles.designGrid} ${variant === 'work' ? styles.designGridWork : styles.designGridGlobal}`;

  const imageSizes =
    variant === 'work'
      ? getImageLayoutSizes({ sp: '30vw', pc: '160px' })
      : getImageLayoutSizes({ sp: '30vw', pc: '180px' });

  return (
    <div className={styles.section}>
      <SectionTitle englishLabel='USER DESIGNS' japaneseLabel='みんなのデザイン' />
      <h2 className={styles.pageTitle}>
        好きな<span className={styles.accentText}>デザイン</span>を探そう
      </h2>
      <p className={styles.subtitle}>これまでにジャンプLABでつくられたグッズデザインです</p>

      {variant === 'work' && (
        <div className={styles.filterSection}>
          <ItemFilterTrigger
            label='アイテムで絞り込む'
            selectedProductName={props.selectedProductName}
            onOpen={props.onOpenItemFilter}
            onClear={props.onRemoveItemFilter}
            disabled={props.isFilterPending}
          />
        </div>
      )}

      {visibleDesigns.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyMessage}>該当するデザインがありません</p>
        </div>
      ) : (
        <div className={gridClassName}>
          {visibleDesigns.map((design) => (
            <UserDesignCard
              key={design.id}
              previewImageUrl={design.previewImageUrl}
              workName={design.gfWorkName}
              onClick={() => handleDesignClick(design)}
              sizes={imageSizes}
            />
          ))}
        </div>
      )}

      <InfiniteScroll
        hasMore={hasMore}
        isLoading={isLoading}
        onLoadMore={loadMore}
        loadingComponent={
          <div className={styles.loadingContainer}>
            <CommonLoading />
          </div>
        }
        sentinelClassName={styles.sentinel}
      />

      <UserDesignDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        items={visibleDesigns}
        initialIndex={selectedIndex}
        getCallbackUrl={getCallbackUrl}
        from={sizeSelectFrom}
      />
    </div>
  );
}

export default UserDesignGridSection;
